/*
 * Lemonade Training Oy copy rights all right reserved.
 * Consent Event Type Definitions (F016 — commitment memberships)
 *
 * Immutable consumer-rights consent evidence, function-written at checkout
 * BEFORE payment. This is an audit record proving express consent to a
 * commitment's terms was captured pre-charge — it is excluded from analytics
 * streams and never client-writable (rules: read record-owner only via
 * userUID match — no box-admin read, the record carries no boxID —
 * write: false).
 */

import { Timestamp } from "firebase/firestore";

// Runtime array is the single source of truth — the union derives from it.
export const CONSENT_LOCALES = ["en", "fi"] as const;
export type ConsentLocale = (typeof CONSENT_LOCALES)[number];

/**
 * Which purchases the consumer-rights consent gate covers. Absent on the wire
 * means "commitment": a server predating the field, or an older revision still
 * serving during a cutover, must be read as the NARROWER setting rather than
 * having a checkbox rendered against it that nothing enforces.
 *
 * Keyed, and imported by BOTH halves rather than restated: the server enforces
 * against these strings and the browser decides whether to draw the checkbox
 * against them. Two hand-written copies is how a rename refuses every monthly
 * checkout with no checkbox on screen to tick (LT-1637).
 */
export const CRD_CONSENT_SCOPES = {
  commitment: "commitment",
  allRecurring: "all_recurring",
} as const;
export type CrdConsentScope =
  (typeof CRD_CONSENT_SCOPES)[keyof typeof CRD_CONSENT_SCOPES];

export type ConsentEvent = {
  /** Explicit null until the subscription exists — never omitted, so "not yet created" is distinguishable from "field lost". */
  subscriptionId: string | null;
  productUID: string;
  priceID: string;
  userUID: string;
  /**
   * Version tags of the exact consent/withdrawal wording shown, so a later
   * legal ruling on wording is remediable without re-consenting existing
   * members.
   */
  consentWordingVersion: string;
  withdrawalInfoVersion: string;
  /** Canonical hashes of the copy shown, server-derived from the copy module. */
  wordingContentHash: string;
  withdrawalInfoContentHash: string;
  /** The consent copy module's own locale set — deliberately independent of GymTermsLocale. */
  locale: ConsentLocale;
  /** Server timestamp — the legal "consented at". */
  consentedAt: Timestamp | Date;
  /** Literal true: consent was captured BEFORE payment. */
  capturedPrePayment: true;
  /**
   * GDPR minimization: HASH the IP (default), or omit per legal guidance —
   * never store the raw IP.
   */
  ipHash?: string;
  userAgent?: string;
};
